<template>
  <main class="page-wrap">
    <UiPageHero
      eyebrow="Quản lý danh mục"
      :title="config.title"
      :description="config.description"
    >
      <template v-if="canManageCurrentCategory" #actions>
        <UiButton variant="white" icon="add" @click="openCreateCategory">
          {{ createButtonLabel }}
        </UiButton>
      </template>
    </UiPageHero>

    <UiPanel>
      <div
        v-if="category === 'device-type-details'"
        class="mb-3 grid max-w-sm grid-cols-1 gap-2"
      >
        <UiSelect v-model.number="selectedGroupId" label="Nhóm thiết bị">
          <option :value="0">Chọn nhóm thiết bị</option>
          <option v-for="item in groups" :key="item.id" :value="item.id">
            {{ item.tenNhomThietBi }}
          </option>
        </UiSelect>
      </div>

      <div class="category-table-block">
        <div
          :class="[
            'table-wrap category-table-wrap',
            loading ? 'is-loading' : '',
          ]"
        >
          <div v-if="loading" class="table-loading-overlay">
            <span class="table-spinner"></span>
            Đang tải dữ liệu...
          </div>

          <table class="data-table category-data-table">
            <thead>
              <tr>
                <th v-for="column in config.columns" :key="column.key">
                  {{ column.label }}
                </th>
                <th v-if="hasRowActions">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!rows.length && !loading">
                <td :colspan="tableColspan" class="text-center">
                  Không có dữ liệu
                </td>
              </tr>
              <tr v-for="(row, index) in paginatedRows" :key="row.id">
                <td v-for="column in config.columns" :key="column.key">
                  {{
                    column.key === "id"
                      ? rowIndex(index)
                      : formatValue(row[column.key])
                  }}
                </td>
                <td v-if="hasRowActions">
                  <div class="inline-flex w-max gap-2">
                    <UiButton
                      v-if="category === 'departments'"
                      type="button"
                      variant="secondary"
                      size="sm"
                      icon="edit"
                      @click="openUpdateDepartment(row)"
                    >
                      Cập nhật
                    </UiButton>
                    <UiButton
                      v-if="category === 'device-groups'"
                      type="button"
                      variant="secondary"
                      size="sm"
                      icon="edit"
                      @click="openUpdateDeviceGroup(row)"
                    >
                      Cập nhật
                    </UiButton>
                    <UiButton
                      v-if="category === 'device-type-details'"
                      type="button"
                      variant="secondary"
                      size="sm"
                      icon="edit"
                      @click="openUpdateDeviceType(row)"
                    >
                      Cập nhật
                    </UiButton>
                    <UiButton
                      v-if="canDeleteCurrentCategory"
                      type="button"
                      variant="danger"
                      size="sm"
                      icon="delete"
                      @click="openDeleteCategory(row)"
                    >
                      Xóa
                    </UiButton>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <UiPagination
          v-model="page"
          :total="rows.length"
          :page-size="pageSize"
        />
      </div>
    </UiPanel>

    <UiModal v-if="showCreateModal">
      <form @submit.prevent="createCategory">
        <div class="mb-4 flex items-start justify-between">
          <div>
            <p class="text-xl font-bold text-ink">{{ createModalTitle }}</p>
            <p class="text-sm text-slate-500">{{ createModalDescription }}</p>
          </div>
          <UiButton variant="ghost" size="sm" icon="close" @click="closeCreateCategory">
            Đóng
          </UiButton>
        </div>

        <div
          v-if="category === 'device-type-details'"
          class="grid gap-3 md:grid-cols-2"
        >
          <UiInput
            v-model="deviceTypeForm.tenLoaiThietBi"
            class="md:col-span-2"
            label="Tên loại thiết bị"
            required
            placeholder="Nhập tên loại thiết bị"
          />
          <UiInput
            v-model.number="deviceTypeForm.thoiGianHaoMon"
            label="Thời gian hao mòn"
            type="number"
            min="0"
            required
            placeholder="Nhập thời gian hao mòn"
          />
          <UiInput
            v-model.number="deviceTypeForm.tyLeHaoMon"
            label="Tỷ lệ hao mòn"
            type="number"
            min="0"
            step="0.01"
            required
            placeholder="Nhập tỷ lệ hao mòn"
          />
          <UiSelect
            v-model.number="deviceTypeForm.idNhomThietBi"
            class="md:col-span-2"
            label="Nhóm thiết bị"
            required
          >
            <option :value="0">Chọn nhóm thiết bị</option>
            <option v-for="item in groups" :key="item.id" :value="item.id">
              {{ item.tenNhomThietBi }}
            </option>
          </UiSelect>
        </div>
        <UiInput
          v-else
          v-model="categoryForm.name"
          :label="formFieldLabel"
          required
          :placeholder="formFieldPlaceholder"
        />

        <div class="mt-4 flex justify-end gap-3">
          <UiButton
            type="button"
            variant="secondary"
            icon="cancel"
            @click="closeCreateCategory"
          >
            Hủy
          </UiButton>
          <UiButton type="submit" icon="confirm" :loading="creatingCategory">
            Xác nhận
          </UiButton>
        </div>
      </form>
    </UiModal>

    <UiModal v-if="categoryToDelete">
      <form @submit.prevent="deleteCategory">
        <div class="mb-4 flex items-start justify-between">
          <div>
            <p class="text-xl font-bold text-ink">{{ deleteModalTitle }}</p>
            <p class="text-sm text-slate-500">{{ categoryDeleteName }}</p>
          </div>
          <UiButton variant="ghost" size="sm" icon="close" @click="closeDeleteCategory">
            Đóng
          </UiButton>
        </div>

        <p class="text-sm font-semibold text-slate-600">
          Bạn có chắc chắn muốn xóa {{ currentItemLabel }}
          <span class="font-bold text-ink">{{ categoryDeleteName }}</span>
          không?
        </p>

        <div class="mt-4 flex justify-end gap-3">
          <UiButton
            type="button"
            variant="secondary"
            icon="cancel"
            @click="closeDeleteCategory"
          >
            Hủy
          </UiButton>
          <UiButton type="submit" variant="danger" icon="delete" :loading="deletingCategory">
            Xác nhận xóa
          </UiButton>
        </div>
      </form>
    </UiModal>

    <UiModal v-if="departmentToUpdate">
      <form @submit.prevent="updateDepartment">
        <div class="mb-4 flex items-start justify-between">
          <div>
            <p class="text-xl font-bold text-ink">Cập nhật khoa phòng</p>
            <p class="text-sm text-slate-500">
              {{ departmentToUpdate.tenKhoaPhong }}
            </p>
          </div>
          <UiButton variant="ghost" size="sm" icon="close" @click="closeUpdateDepartment">
            Đóng
          </UiButton>
        </div>

        <UiInput
          v-model="departmentUpdateForm.tenKhoaPhong"
          label="Tên khoa phòng"
          required
          placeholder="Nhập tên khoa phòng"
        />

        <div class="mt-4 flex justify-end gap-3">
          <UiButton
            type="button"
            variant="secondary"
            icon="cancel"
            @click="closeUpdateDepartment"
          >
            Hủy
          </UiButton>
          <UiButton type="submit" icon="save" :loading="updatingDepartment">
            Xác nhận
          </UiButton>
        </div>
      </form>
    </UiModal>

    <UiModal v-if="deviceGroupToUpdate">
      <form @submit.prevent="updateDeviceGroup">
        <div class="mb-4 flex items-start justify-between">
          <div>
            <p class="text-xl font-bold text-ink">Cập nhật nhóm thiết bị</p>
            <p class="text-sm text-slate-500">
              {{ deviceGroupToUpdate.tenNhomThietBi }}
            </p>
          </div>
          <UiButton variant="ghost" size="sm" icon="close" @click="closeUpdateDeviceGroup">
            Đóng
          </UiButton>
        </div>

        <UiInput
          v-model="deviceGroupUpdateForm.tenNhomThietBi"
          label="Tên nhóm thiết bị"
          required
          placeholder="Nhập tên nhóm thiết bị"
        />

        <div class="mt-4 flex justify-end gap-3">
          <UiButton
            type="button"
            variant="secondary"
            icon="cancel"
            @click="closeUpdateDeviceGroup"
          >
            Hủy
          </UiButton>
          <UiButton type="submit" icon="save" :loading="updatingDeviceGroup">
            Xác nhận
          </UiButton>
        </div>
      </form>
    </UiModal>

    <UiModal v-if="deviceTypeToUpdate">
      <form @submit.prevent="updateDeviceType">
        <div class="mb-4 flex items-start justify-between">
          <div>
            <p class="text-xl font-bold text-ink">
              Cập nhật chi tiết loại thiết bị
            </p>
            <p class="text-sm text-slate-500">
              {{ deviceTypeToUpdate.tenLoaiThietBi }}
            </p>
          </div>
          <UiButton variant="ghost" size="sm" icon="close" @click="closeUpdateDeviceType">
            Đóng
          </UiButton>
        </div>

        <div class="grid gap-3 md:grid-cols-2">
          <UiInput
            v-model="deviceTypeUpdateForm.tenLoaiThietBi"
            class="md:col-span-2"
            label="Tên loại thiết bị"
            required
            placeholder="Nhập tên loại thiết bị"
          />
          <UiInput
            v-model.number="deviceTypeUpdateForm.thoiGianHaoMon"
            label="Thời gian hao mòn"
            type="number"
            min="0"
            required
            placeholder="Nhập thời gian hao mòn"
          />
          <UiInput
            v-model.number="deviceTypeUpdateForm.tyLeHaoMon"
            label="Tỷ lệ hao mòn"
            type="number"
            min="0"
            step="0.01"
            required
            placeholder="Nhập tỷ lệ hao mòn"
          />
          <UiSelect
            v-model.number="deviceTypeUpdateForm.idNhomThietBi"
            class="md:col-span-2"
            label="Nhóm thiết bị"
            required
          >
            <option :value="0">Chọn nhóm thiết bị</option>
            <option v-for="item in groups" :key="item.id" :value="item.id">
              {{ item.tenNhomThietBi }}
            </option>
          </UiSelect>
        </div>

        <div class="mt-4 flex justify-end gap-3">
          <UiButton
            type="button"
            variant="secondary"
            icon="cancel"
            @click="closeUpdateDeviceType"
          >
            Hủy
          </UiButton>
          <UiButton type="submit" icon="save" :loading="updatingDeviceType">
            Xác nhận
          </UiButton>
        </div>
      </form>
    </UiModal>
  </main>
</template>

<script lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import QLTSRepository from "@/services/QLTSRepository";
import { KhoaPhong } from "@/models/khoaPhong";
import { NhomThietBi } from "@/models/nhomThietBi";
import { ChiTietLoaiThietBi } from "@/models/chiTietLoaiThietBi";
import { getErrorMessage, useToast } from "@/utils/toast";

type CategoryKey = "departments" | "device-groups" | "device-type-details";

type Column = {
  key: string;
  label: string;
};

type CategoryConfig = {
  title: string;
  description: string;
  itemLabel: string;
  createButtonLabel?: string;
  columns: Column[];
};

type ManagedCategoryKey =
  | "departments"
  | "device-groups"
  | "device-type-details";
type DeletableCategoryKey = "device-groups" | "device-type-details";

const configs: Record<CategoryKey, CategoryConfig> = {
  departments: {
    title: "Khoa phòng",
    description: "Danh sách khoa phòng đang được dùng trong hệ thống.",
    itemLabel: "khoa phòng",
    createButtonLabel: "Tạo khoa phòng",
    columns: [
      { key: "id", label: "STT" },
      { key: "tenKhoaPhong", label: "Tên khoa phòng" },
    ],
  },
  "device-groups": {
    title: "Nhóm thiết bị",
    description: "Danh sách nhóm thiết bị phục vụ phân loại tài sản.",
    itemLabel: "nhóm thiết bị",
    createButtonLabel: "Tạo nhóm thiết bị",
    columns: [
      { key: "id", label: "STT" },
      { key: "tenNhomThietBi", label: "Tên nhóm thiết bị" },
    ],
  },
  "device-type-details": {
    title: "Chi tiết loại thiết bị",
    description: "Danh sách loại thiết bị theo từng nhóm thiết bị.",
    itemLabel: "chi tiết loại thiết bị",
    createButtonLabel: "Tạo chi tiết loại thiết bị",
    columns: [
      { key: "id", label: "STT" },
      { key: "tenLoaiThietBi", label: "Tên loại thiết bị" },
      { key: "thoiGianHaoMon", label: "Thời gian hao mòn" },
      { key: "tyLeHaoMon", label: "Tỷ lệ hao mòn" },
    ],
  },
};

const managedCategories: ManagedCategoryKey[] = [
  "departments",
  "device-groups",
  "device-type-details",
];
const deletableCategories: DeletableCategoryKey[] = [
  "device-groups",
  "device-type-details",
];

export default {
  setup() {
    const route = useRoute();
    const { showToast } = useToast();
    const loading = ref(false);
    const rows = ref<Array<Record<string, any>>>([]);
    const page = ref(1);
    const pageSize = 10;
    const groups = ref<NhomThietBi[]>([]);
    const selectedGroupId = ref(0);
    const suppressSelectedGroupWatch = ref(false);
    const showCreateModal = ref(false);
    const creatingCategory = ref(false);
    const deletingCategory = ref(false);
    const updatingDepartment = ref(false);
    const updatingDeviceGroup = ref(false);
    const updatingDeviceType = ref(false);
    const categoryToDelete = ref<Record<string, any> | null>(null);
    const departmentToUpdate = ref<KhoaPhong | null>(null);
    const deviceGroupToUpdate = ref<NhomThietBi | null>(null);
    const deviceTypeToUpdate = ref<ChiTietLoaiThietBi | null>(null);
    const categoryForm = ref({
      name: "",
    });
    const deviceGroupUpdateForm = ref({
      id: 0,
      tenNhomThietBi: "",
    });
    const departmentUpdateForm = ref({
      id: 0,
      tenKhoaPhong: "",
    });
    const deviceTypeForm = ref({
      tenLoaiThietBi: "",
      thoiGianHaoMon: 0,
      tyLeHaoMon: 0,
      idNhomThietBi: 0,
    });
    const deviceTypeUpdateForm = ref({
      id: 0,
      tenLoaiThietBi: "",
      thoiGianHaoMon: 0,
      tyLeHaoMon: 0,
      idNhomThietBi: 0,
    });

    const category = computed(
      () => (route.meta.category as CategoryKey) || "departments",
    );
    const config = computed(() => configs[category.value]);
    const canManageCurrentCategory = computed(() =>
      managedCategories.includes(category.value as ManagedCategoryKey),
    );
    const canDeleteCurrentCategory = computed(() =>
      deletableCategories.includes(category.value as DeletableCategoryKey),
    );
    const hasRowActions = computed(
      () =>
        category.value === "departments" ||
        category.value === "device-groups" ||
        category.value === "device-type-details",
    );
    const currentItemLabel = computed(() => config.value.itemLabel);
    const createButtonLabel = computed(
      () => config.value.createButtonLabel || `Tạo ${currentItemLabel.value}`,
    );
    const createModalTitle = computed(() => createButtonLabel.value);
    const createModalDescription = computed(
      () => `Nhập tên ${currentItemLabel.value} cần thêm vào danh mục.`,
    );
    const deleteModalTitle = computed(() => `Xóa ${currentItemLabel.value}`);
    const formFieldLabel = computed(() => `Tên ${currentItemLabel.value}`);
    const formFieldPlaceholder = computed(
      () => `Nhập tên ${currentItemLabel.value}`,
    );
    const tableColspan = computed(
      () => config.value.columns.length + (hasRowActions.value ? 1 : 0),
    );
    const pageCount = computed(() =>
      Math.max(1, Math.ceil(rows.value.length / pageSize)),
    );
    const paginatedRows = computed(() => {
      const start = (page.value - 1) * pageSize;
      return rows.value.slice(start, start + pageSize);
    });
    const categoryDeleteName = computed(() => {
      if (!categoryToDelete.value) return "";
      if (category.value === "departments") {
        return categoryToDelete.value.tenKhoaPhong || "";
      }
      if (category.value === "device-groups") {
        return categoryToDelete.value.tenNhomThietBi || "";
      }
      if (category.value === "device-type-details") {
        return categoryToDelete.value.tenLoaiThietBi || "";
      }
      return "";
    });

    const loadGroups = async () => {
      const res = await QLTSRepository.getListNhomThietBi();
      groups.value = res.data || [];
      if (!selectedGroupId.value && groups.value.length) {
        suppressSelectedGroupWatch.value = true;
        selectedGroupId.value = groups.value[0].id;
      }
    };

    const loadRows = async () => {
      loading.value = true;
      try {
        if (category.value === "departments") {
          const res = await QLTSRepository.getListKhoaPhong();
          rows.value = (res.data || []) as KhoaPhong[];
          return;
        }

        if (category.value === "device-groups") {
          const res = await QLTSRepository.getListNhomThietBi();
          rows.value = (res.data || []) as NhomThietBi[];
          return;
        }

        if (!groups.value.length) {
          await loadGroups();
        }

        if (!selectedGroupId.value) {
          rows.value = [];
          return;
        }

        const res = await QLTSRepository.getListChiTietLoaiThietBi(
          selectedGroupId.value,
        );
        rows.value = (res.data || []) as ChiTietLoaiThietBi[];
      } catch (err) {
        showToast(getErrorMessage(err, "Không tải được danh mục."), "error");
        rows.value = [];
      } finally {
        if (page.value > pageCount.value) {
          page.value = pageCount.value;
        }
        loading.value = false;
      }
    };

    const formatValue = (value: unknown) => {
      if (value === null || value === undefined || value === "") return "-";
      return value;
    };
    const rowIndex = (index: number) => (page.value - 1) * pageSize + index + 1;

    const openCreateCategory = () => {
      categoryForm.value.name = "";
      deviceTypeForm.value = {
        tenLoaiThietBi: "",
        thoiGianHaoMon: 0,
        tyLeHaoMon: 0,
        idNhomThietBi: selectedGroupId.value || groups.value[0]?.id || 0,
      };
      showCreateModal.value = true;
    };

    const closeCreateCategory = () => {
      if (creatingCategory.value) return;
      showCreateModal.value = false;
    };

    const createCategory = async () => {
      if (category.value === "device-type-details") {
        const tenLoaiThietBi = deviceTypeForm.value.tenLoaiThietBi.trim();
        if (!tenLoaiThietBi) {
          showToast("Vui lòng nhập tên loại thiết bị.", "error");
          return;
        }
        if (!deviceTypeForm.value.idNhomThietBi) {
          showToast("Vui lòng chọn nhóm thiết bị.", "error");
          return;
        }

        creatingCategory.value = true;
        try {
          await QLTSRepository.createChiTietLoaiThietBi({
            tenLoaiThietBi,
            thoiGianHaoMon: Number(deviceTypeForm.value.thoiGianHaoMon) || 0,
            tyLeHaoMon: Number(deviceTypeForm.value.tyLeHaoMon) || 0,
            idNhomThietBi: Number(deviceTypeForm.value.idNhomThietBi),
          });
          showToast("Tạo chi tiết loại thiết bị thành công.", "success");
          showCreateModal.value = false;
          if (
            selectedGroupId.value !== Number(deviceTypeForm.value.idNhomThietBi)
          ) {
            suppressSelectedGroupWatch.value = true;
            selectedGroupId.value = Number(deviceTypeForm.value.idNhomThietBi);
          }
          await loadRows();
        } catch (err) {
          showToast(
            getErrorMessage(err, "Không tạo được chi tiết loại thiết bị."),
            "error",
          );
        } finally {
          creatingCategory.value = false;
        }
        return;
      }

      const name = categoryForm.value.name.trim();
      if (!name) {
        showToast(`Vui lòng nhập tên ${currentItemLabel.value}.`, "error");
        return;
      }

      creatingCategory.value = true;
      try {
        if (category.value === "departments") {
          await QLTSRepository.createKhoaPhong({ tenKhoaPhong: name });
        } else if (category.value === "device-groups") {
          await QLTSRepository.createNhomThietBi({
            id: 0,
            tenNhomThietBi: name,
          });
        }

        showToast(`Tạo ${currentItemLabel.value} thành công.`, "success");
        showCreateModal.value = false;
        await loadRows();
      } catch (err) {
        showToast(
          getErrorMessage(err, `Không tạo được ${currentItemLabel.value}.`),
          "error",
        );
      } finally {
        creatingCategory.value = false;
      }
    };

    const openDeleteCategory = (row: Record<string, any>) => {
      categoryToDelete.value = row;
    };

    const closeDeleteCategory = () => {
      if (deletingCategory.value) return;
      categoryToDelete.value = null;
    };

    const openUpdateDepartment = (row: Record<string, any>) => {
      const item = row as KhoaPhong;
      departmentToUpdate.value = item;
      departmentUpdateForm.value = {
        id: item.id,
        tenKhoaPhong: item.tenKhoaPhong || "",
      };
    };

    const closeUpdateDepartment = () => {
      if (updatingDepartment.value) return;
      departmentToUpdate.value = null;
    };

    const updateDepartment = async () => {
      const tenKhoaPhong = departmentUpdateForm.value.tenKhoaPhong.trim();
      if (!tenKhoaPhong) {
        showToast("Vui lòng nhập tên khoa phòng.", "error");
        return;
      }

      updatingDepartment.value = true;
      try {
        await QLTSRepository.updateKhoaPhong({
          id: departmentUpdateForm.value.id,
          tenKhoaPhong,
        });
        showToast("Cập nhật khoa phòng thành công.", "success");
        departmentToUpdate.value = null;
        await loadRows();
      } catch (err) {
        showToast(
          getErrorMessage(err, "Không cập nhật được khoa phòng."),
          "error",
        );
      } finally {
        updatingDepartment.value = false;
      }
    };

    const openUpdateDeviceGroup = (row: Record<string, any>) => {
      const item = row as NhomThietBi;
      deviceGroupToUpdate.value = item;
      deviceGroupUpdateForm.value = {
        id: item.id,
        tenNhomThietBi: item.tenNhomThietBi || "",
      };
    };

    const closeUpdateDeviceGroup = () => {
      if (updatingDeviceGroup.value) return;
      deviceGroupToUpdate.value = null;
    };

    const updateDeviceGroup = async () => {
      const tenNhomThietBi = deviceGroupUpdateForm.value.tenNhomThietBi.trim();
      if (!tenNhomThietBi) {
        showToast("Vui lòng nhập tên nhóm thiết bị.", "error");
        return;
      }

      updatingDeviceGroup.value = true;
      try {
        await QLTSRepository.updateNhomThietBi({
          id: deviceGroupUpdateForm.value.id,
          tenNhomThietBi,
        });
        showToast("Cập nhật nhóm thiết bị thành công.", "success");
        deviceGroupToUpdate.value = null;
        await loadRows();
      } catch (err) {
        showToast(
          getErrorMessage(err, "Không cập nhật được nhóm thiết bị."),
          "error",
        );
      } finally {
        updatingDeviceGroup.value = false;
      }
    };

    const openUpdateDeviceType = (row: Record<string, any>) => {
      const item = row as ChiTietLoaiThietBi;
      deviceTypeToUpdate.value = item;
      deviceTypeUpdateForm.value = {
        id: item.id,
        tenLoaiThietBi: item.tenLoaiThietBi || "",
        thoiGianHaoMon: Number(item.thoiGianHaoMon) || 0,
        tyLeHaoMon: Number(item.tyLeHaoMon) || 0,
        idNhomThietBi: Number(item.idNhomThietBi || selectedGroupId.value) || 0,
      };
    };

    const closeUpdateDeviceType = () => {
      if (updatingDeviceType.value) return;
      deviceTypeToUpdate.value = null;
    };

    const updateDeviceType = async () => {
      const tenLoaiThietBi = deviceTypeUpdateForm.value.tenLoaiThietBi.trim();
      if (!tenLoaiThietBi) {
        showToast("Vui lòng nhập tên loại thiết bị.", "error");
        return;
      }
      if (!deviceTypeUpdateForm.value.idNhomThietBi) {
        showToast("Vui lòng chọn nhóm thiết bị.", "error");
        return;
      }

      updatingDeviceType.value = true;
      try {
        await QLTSRepository.updateChiTietLoaiThietBi({
          id: Number(deviceTypeUpdateForm.value.id),
          tenLoaiThietBi,
          thoiGianHaoMon:
            Number(deviceTypeUpdateForm.value.thoiGianHaoMon) || 0,
          tyLeHaoMon: Number(deviceTypeUpdateForm.value.tyLeHaoMon) || 0,
          idNhomThietBi: Number(deviceTypeUpdateForm.value.idNhomThietBi),
        });
        showToast("Cập nhật chi tiết loại thiết bị thành công.", "success");
        if (
          selectedGroupId.value !==
          Number(deviceTypeUpdateForm.value.idNhomThietBi)
        ) {
          suppressSelectedGroupWatch.value = true;
          selectedGroupId.value = Number(
            deviceTypeUpdateForm.value.idNhomThietBi,
          );
        }
        deviceTypeToUpdate.value = null;
        await loadRows();
      } catch (err) {
        showToast(
          getErrorMessage(err, "Không cập nhật được chi tiết loại thiết bị."),
          "error",
        );
      } finally {
        updatingDeviceType.value = false;
      }
    };

    const deleteCategory = async () => {
      if (!categoryToDelete.value) return;

      deletingCategory.value = true;
      try {
        if (category.value === "departments") {
          await QLTSRepository.deleteKhoaPhong(categoryToDelete.value.id);
        } else if (category.value === "device-groups") {
          await QLTSRepository.deleteNhomThietBi(categoryToDelete.value.id);
        } else if (category.value === "device-type-details") {
          await QLTSRepository.deleteChiTietLoaiThietBi(
            categoryToDelete.value.id,
          );
        }

        showToast(`Xóa ${currentItemLabel.value} thành công.`, "success");
        categoryToDelete.value = null;
        await loadRows();
      } catch (err) {
        showToast(
          getErrorMessage(err, `Không xóa được ${currentItemLabel.value}.`),
          "error",
        );
      } finally {
        deletingCategory.value = false;
      }
    };

    onMounted(loadRows);
    watch(category, () => {
      page.value = 1;
      showCreateModal.value = false;
      categoryToDelete.value = null;
      departmentToUpdate.value = null;
      deviceGroupToUpdate.value = null;
      deviceTypeToUpdate.value = null;
      loadRows();
    });
    watch(selectedGroupId, () => {
      if (suppressSelectedGroupWatch.value) {
        suppressSelectedGroupWatch.value = false;
        return;
      }
      if (category.value === "device-type-details") {
        page.value = 1;
        loadRows();
      }
    });
    watch(rows, () => {
      if (page.value > pageCount.value) {
        page.value = pageCount.value;
      }
    });

    return {
      canManageCurrentCategory,
      canDeleteCurrentCategory,
      category,
      categoryDeleteName,
      categoryForm,
      categoryToDelete,
      closeCreateCategory,
      closeDeleteCategory,
      closeUpdateDepartment,
      closeUpdateDeviceGroup,
      closeUpdateDeviceType,
      config,
      createButtonLabel,
      createCategory,
      createModalDescription,
      createModalTitle,
      creatingCategory,
      currentItemLabel,
      deleteCategory,
      deleteModalTitle,
      deletingCategory,
      departmentToUpdate,
      departmentUpdateForm,
      deviceGroupToUpdate,
      deviceGroupUpdateForm,
      deviceTypeToUpdate,
      deviceTypeForm,
      deviceTypeUpdateForm,
      formFieldLabel,
      formFieldPlaceholder,
      formatValue,
      groups,
      hasRowActions,
      loading,
      openCreateCategory,
      openDeleteCategory,
      openUpdateDepartment,
      openUpdateDeviceGroup,
      openUpdateDeviceType,
      page,
      pageSize,
      paginatedRows,
      rowIndex,
      rows,
      selectedGroupId,
      showCreateModal,
      tableColspan,
      updateDepartment,
      updateDeviceGroup,
      updateDeviceType,
      updatingDepartment,
      updatingDeviceGroup,
      updatingDeviceType,
    };
  },
};
</script>

<style scoped>
.category-table-block {
  width: fit-content;
  max-width: 100%;
}

.category-table-wrap {
  width: fit-content;
  max-width: 100%;
}

.category-data-table {
  width: max-content;
  min-width: min(760px, calc(100vw - 72px));
}

.category-data-table th,
.category-data-table td {
  min-width: 150px;
}

.category-data-table th:first-child,
.category-data-table td:first-child {
  width: 88px;
  min-width: 88px;
}

.category-data-table th:last-child,
.category-data-table td:last-child {
  width: 140px;
  min-width: 140px;
}
</style>
